import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { AngularDateHttpInterceptor, UserState, DIARY_ITEM_FACTORY, BASE_URL, AUTH_SCOPES, CORE_API_URL, DIARY_HUB_URL, DiaryState, EMAIL_TEMPLATE_HUB_URL, EMAIL_HUB_URL, MESSAGING_ENDPOINT, MessagesState, MEDICAL_HUB_URL, MedicalService, DOMAIN, BlockUIState, TemplateEmailState, SYSTEM_USER_ID } from '@harlowe/core';
import { DRAFT_EMAIL_ITEM_FACTORY } from './components/main/email-sidebar/email-sidebar.component'; 
import { TealCompMedicalService } from './services/medical.service';
import { BrowserModule } from '@angular/platform-browser';
import { NgxsModule } from '@ngxs/store';
import { environment } from '../environments/environment';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { MenuModule } from 'primeng/menu';
import { ConfirmationService, MessageService, OverlayService } from 'primeng/api';
import { ReferralState } from './state/referrals/referral-state';
import { EncounterState } from './state/encounters/encounter-state';
import { UserInterfaceState } from './state/ui/ui-state';
import { EditorModule } from 'primeng/editor';
import { ClaimState } from './state/claims/claim-state';
import { EncounterDiaryManagerItemFactory } from './components/encounter/encounter-editor/encounter-diary-manager';
import { EncounterDraftEmailManagerItemFactory } from './components/main/email-sidebar/encounter-draft-email-manager';
import { EmailState } from './state/encounter-email/email-state';

import 'reflect-metadata';

//MSAL Functions

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.harlowe.tealcomp.auth.clientId,
      authority: environment.harlowe.tealcomp.auth.authority,
      redirectUri: environment.harlowe.tealcomp.auth.redirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}
  
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.harlowe.tealcomp.baseUri, environment.harlowe.tealcomp.auth.scopes);
  protectedResourceMap.set(environment.harlowe.tealcomp.coreApiUri, environment.harlowe.tealcomp.auth.scopes);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap
  };
  }
  
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: environment.harlowe.tealcomp.auth.scopes
    }
    };
  }


export const appConfig: ApplicationConfig = 
{
  providers: [

    provideRouter(routes),
    // provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(),withFetch()),
    
    //For @harlowe/core api services
    { provide: BASE_URL, useValue: environment.harlowe.tealcomp.baseUri },
    { provide: CORE_API_URL, useValue: environment.harlowe.tealcomp.coreApiUri },
    { provide: AUTH_SCOPES, useValue: environment.harlowe.tealcomp.auth.scopes},
    { provide: DIARY_HUB_URL, useValue: environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.encounterDiaries},
    { provide: EMAIL_TEMPLATE_HUB_URL, useValue: environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.emailTemplates},
    { provide: EMAIL_HUB_URL, useValue: environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.email},
    { provide: MESSAGING_ENDPOINT, useValue: environment.harlowe.tealcomp.signaling.endpoints.messaging },
    { provide: DOMAIN, useValue: environment.harlowe.tealcomp.domain },
    { provide: SYSTEM_USER_ID, useValue: environment.systemUserId },

    //MSAL Stuff
    {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
    },
    {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ConfirmationService,
    //Date Intercepter
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AngularDateHttpInterceptor,
      multi: true
    },
    //NGSX
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      OverlayService,
      MenuModule,
      EditorModule,
      // UserModule,
      NgxsModule.forRoot([
        UserState, 
        MessagesState, 
        ReferralState, 
        EncounterState, 
        UserInterfaceState, 
        BlockUIState, 
        ClaimState, 
        DiaryState, 
        TemplateEmailState, 
        EmailState 
      ], {
          developmentMode: !environment.production,
      }),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled: environment.production
    }))
    ,

    
    { provide: DIARY_ITEM_FACTORY, useClass: EncounterDiaryManagerItemFactory},
    { provide: DRAFT_EMAIL_ITEM_FACTORY, useClass: EncounterDraftEmailManagerItemFactory},
    { provide: MEDICAL_HUB_URL, useValue: environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.medicalBills },
    { provide: MedicalService, useExisting: TealCompMedicalService},
    //Provide API Service
    // { provide: ApiService },
    // { provide: UserService },
    // { provide: MessagingService, useClass: MessagingServiceImpl},
    // { provide: SignalingService, useClass: SignalingServiceImpl},
    // { provide: ReferralService, useClass: ReferralServiceImpl},
    // { provide: PartyService, useClass: PartyServiceImpl},
    // { provide: ClaimService, useClass: ClaimServiceImpl},
    // { provide: MedicalService, useClass: MedicalServiceImpl},
    // { provide: EncounterService, useClass: EncounterServiceServiceImpl},
    // { provide: LocationService, useClass: LocationServiceImpl},
    // { provide: UtilService, useClass: UtilServiceImpl},
    // { provide: FinanceService, useClass: FinanceServiceServiceImpl},
    // { provide: DiaryService, useClass: DiaryServiceImpl }
    // { provide: EmailService, useClass: EmailServiceImpl}
]
};
